/**
 * Background DR-node check (palapian-control).
 *
 * Polls /api/node-health every 15 s. On standalone installs the endpoint says
 * `monitored: false` once and the component goes fully dormant. On a DR node it
 * warns — loudly but non-blocking (operators may legitimately need the UI):
 *   - role !== ACTIVE  -> red banner: this is the standby/fenced system,
 *     changes here will be overwritten by the next sync.
 *   - maintenance      -> amber banner: deploy window in progress.
 * Fixed overlay above the shell so it shows on every page including login.
 */
import { Alert } from "antd";
import { useEffect, useRef, useState } from "react";

import { api, type NodeHealth } from "../api/client";

const POLL_MS = 15_000;

export const NodeLivenessBanner = () => {
  const [health, setHealth] = useState<NodeHealth | null>(null);
  const dormant = useRef(false);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const probe = async () => {
      if (dormant.current) return;
      try {
        const h = await api.nodeHealth();
        if (h.monitored === false) {
          // Standalone install — stop polling for good.
          dormant.current = true;
          if (timer) clearInterval(timer);
          setHealth(null);
          return;
        }
        setHealth(h);
      } catch {
        // Admin backend hiccup — keep the last known state, never break the UI.
      }
    };
    void probe();
    timer = setInterval(() => void probe(), POLL_MS);
    return () => {
      if (timer) clearInterval(timer);
    };
  }, []);

  if (!health || !health.reachable) return null;

  const notLive = health.role != null && health.role !== "ACTIVE";
  const maintenance = health.maintenance === true;
  if (!notLive && !maintenance) return null;

  return (
    <div style={{ position: "fixed", top: 0, insetInline: 0, zIndex: 2000 }}>
      {notLive && (
        <Alert
          banner
          type="error"
          showIcon
          message={
            <b>
              This is the {health.role === "ERROR" ? "FENCED" : "STANDBY"} system — not the
              live one. Anything changed here will be overwritten by the next sync.
            </b>
          }
        />
      )}
      {maintenance && !notLive && (
        <Alert
          banner
          type="warning"
          showIcon
          message={<b>Maintenance window in progress on this node (deploy running).</b>}
        />
      )}
    </div>
  );
};
